import * as CookieManager from "./managers/CookieManager";
import { ModeAwareStorageManager, modeAwareStorage } from './managers/ModeAwareStorageManager';
import { getDebugParams, getDebugDate, isDebugDateMode, setDebugDate } from "./debugUtils";
import { clearGameState } from "@/components/game/GamePersistenceV2";
import { timeoutManager } from "./managers/TimeoutManager";


interface DateCache {
  dates: Map<string, Date>;
  lastCleared: number;
}

const dateCache: DateCache = {
  dates: new Map(),
  lastCleared: Date.now()
};

// Clear cache every hour to prevent memory leaks
const clearCacheIfNeeded = () => {
  const now = Date.now();
  if (now - dateCache.lastCleared > 3600000) { // 1 hour
    dateCache.dates.clear();
    dateCache.lastCleared = now;
  }
};

export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
// Removed: Debug toast for daily mode date check
const maybeShowDateCheckToast = (_date: Date) => {
  // no-op
};

export const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export const isSameDay = (date1: Date, date2: Date): boolean => {
  return formatDate(date1) === formatDate(date2);
};

export const isConsecutiveDay = (date1: Date, date2: Date): boolean => {
  const d1 = normalizeDate(date1);
  const d2 = normalizeDate(date2);

  // Robust across DST changes: add one calendar day to d1 and compare dates
  const nextOfD1 = new Date(d1);
  nextOfD1.setDate(nextOfD1.getDate() + 1);
  const nextNormalized = normalizeDate(nextOfD1);

  const isConsecutive = formatDate(nextNormalized) === formatDate(d2);

  console.log('Checking consecutive days (DST-safe):', {
    d1: d1.toISOString(),
    d2: d2.toISOString(),
    nextOfD1: nextNormalized.toISOString(),
    formattedD1: formatDate(d1),
    formattedD2: formatDate(d2),
    isConsecutive,
    timestamp: new Date().toISOString()
  });

  return isConsecutive;
};

export const getEffectiveDate = (): Date => {
  clearCacheIfNeeded();

  if (isDebugDateMode()) {
    const debugDate = getDebugDate();
    if (debugDate) {
      console.log('Debug mode active - using debug date:', {
        date: debugDate.toISOString(),
        source: 'getDebugDate()',
        timestamp: new Date().toISOString()
      });
      
      const normalized = normalizeDate(debugDate);
      maybeShowDateCheckToast(normalized);
      return normalized;
    }
    
    // Default debug date to current local date when entering debug mode without a stored date
    const nowLocal = new Date(new Date().toLocaleString('en-US', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
    const normalizedNow = normalizeDate(nowLocal);
    try {
      setDebugDate(normalizedNow);
      console.log('Debug mode active with no stored date - defaulting and persisting current date:', {
        normalizedNow: normalizedNow.toISOString(),
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.warn('Failed to persist default debug date; continuing with current date', e);
    }
    maybeShowDateCheckToast(normalizedNow);
    return normalizedNow;
  }

  const now = new Date();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const localDate = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));
  const normalized = normalizeDate(localDate);
  maybeShowDateCheckToast(normalized);
  return normalized;
};

export const isNewDay = (): boolean => {
  // Use mode-aware storage for persistence date
  const lastPlayed = modeAwareStorage.getCookie('game_persistence_date');
  if (!lastPlayed) return true;
  
  const effectiveDate = getEffectiveDate();
  const today = formatDate(effectiveDate);
  return lastPlayed !== today;
};

// Atomic lock to prevent concurrent new day processing
let newDayProcessing = false;
let newDayPromise: Promise<{ isNewDay: boolean; effectiveDate: Date }> | null = null;
let dailyResetBarrier: Promise<void> | null = null;

export const handleNewDay = async (): Promise<{ isNewDay: boolean; effectiveDate: Date }> => {
  // If already processing, wait for the existing operation
  if (newDayProcessing && newDayPromise) {
    console.log('New day processing already in progress, waiting for completion');
    return await newDayPromise;
  }
  
  // If daily reset barrier exists, wait for it
  if (dailyResetBarrier) {
    console.log('[handleNewDay] Waiting for existing daily reset barrier...');
    await dailyResetBarrier;
    return { isNewDay: false, effectiveDate: getEffectiveDate() };
  }

  const effectiveDate = getEffectiveDate();
  // CRITICAL FIX: Always read lastPlayed from DAILY mode to prevent cross-mode confusion
  const lastPlayed = modeAwareStorage.getCookieForMode('game_persistence_date', 'daily');
  const debugMode = isDebugDateMode();
  const currentMode = (localStorage.getItem('babblegram_game_mode') || 'daily') as 'daily' | 'practice';
  
  console.log('📅 HANDLENEWDAY START:', {
    effectiveDate: effectiveDate.toISOString(),
    lastPlayedFromDaily: lastPlayed,
    currentUIMode: currentMode,
    debugMode,
    timestamp: new Date().toISOString()
  });

  // Set atomic lock and create promise with barrier
  newDayProcessing = true;
  newDayPromise = processNewDay(effectiveDate, lastPlayed, debugMode, currentMode);
  
  // Create barrier that blocks all saves/loads until clearing is complete
  dailyResetBarrier = newDayPromise.then(() => {});
  
  try {
    const result = await newDayPromise;
    return result;
  } finally {
    // Clear lock and promise
    newDayProcessing = false;
    newDayPromise = null;
    dailyResetBarrier = null;
  }
};

const processNewDay = async (
  effectiveDate: Date,
  lastPlayed: string | null,
  debugMode: boolean,
  currentMode: 'daily' | 'practice'
): Promise<{ isNewDay: boolean; effectiveDate: Date }> => {
  
  console.log('Handling new day check:', {
    lastPlayed,
    effectiveDate: effectiveDate.toISOString(),
    debugMode,
    currentMode,
    debugDate: debugMode ? getDebugDate()?.toISOString() : null,
    timestamp: new Date().toISOString()
  });
  
  try {
    if (debugMode) {
      const debugDate = getDebugDate();
      
      if (debugDate && (!lastPlayed || formatDate(debugDate) !== lastPlayed)) {
        console.log('Debug date change detected:', {
          debugDate: debugDate.toISOString(),
          lastPlayed,
          effectiveDate: effectiveDate.toISOString(),
          currentMode,
          willClearState: currentMode === 'daily',
          timestamp: new Date().toISOString()
        });
        
        // Only clear game state for daily mode - practice mode should persist
        if (currentMode === 'daily') {
          console.log('Clearing daily mode game state due to debug date change');
          await clearGameState();
        } else {
          console.log('Skipping game state clear - practice mode should persist across days');
        }
        return { isNewDay: true, effectiveDate };
      }
    }
    
    if (!lastPlayed) {
      console.log('No last played date found, treating as new day');
      return { isNewDay: true, effectiveDate };
    }

    const today = formatDate(effectiveDate);
    const isNewDay = lastPlayed !== today;

    console.log('New day check result:', {
      lastPlayed,
      today,
      isNewDay,
      currentMode,
      willClearState: isNewDay && currentMode === 'daily',
      effectiveDate: effectiveDate.toISOString(),
      debugMode,
      debugDate: debugMode ? getDebugDate()?.toISOString() : null,
      timestamp: new Date().toISOString()
    });

    if (isNewDay) {
      // Only clear game state for daily mode - practice mode should persist across days
      if (currentMode === 'daily') {
        console.log('📅 NEW DAY DETECTED: Clearing daily mode game state and history', {
          currentMode,
          today,
          lastPlayed,
          reason: lastPlayed ? 'Date mismatch' : 'Missing lastPlayed',
          timestamp: new Date().toISOString()
        });
        
        await clearGameState();
        
        // ATOMIC CLEAR: Force clear guess history and related cookies to prevent contamination
        console.log('📅 ATOMIC CLEAR: Force clearing guess history and related cookies');
        modeAwareStorage.removeCookieForMode('guess_history', 'daily');
        modeAwareStorage.removeCookieForMode('level_attempts', 'daily');
        modeAwareStorage.removeCookieForMode('keyboard_status', 'daily');
        modeAwareStorage.removeCookieForMode('previous_word', 'daily');
        modeAwareStorage.removeCookieForMode('current_word', 'daily');
        modeAwareStorage.removeCookieForMode('current_level', 'daily');
        modeAwareStorage.removeCookieForMode('game_status', 'daily');
        modeAwareStorage.removeCookieForMode('game_completion_state', 'daily');
        console.log('📅 ATOMIC CLEAR: All guess history cookies cleared for daily mode');
        
        // NOW set persistence date AFTER clearing to prevent race conditions
        modeAwareStorage.setCookie('game_persistence_date', today);
        console.log('📅 PERSISTENCE DATE SET: After clearing state:', today);
        
        // Validate that clearing worked properly - check only streak cookies remain
        const remainingCookies = validateStreakOnlyRemaining(currentMode);
        if (remainingCookies.unexpectedCookies.length > 0) {
          console.warn('📅 UNEXPECTED COOKIES after clearing:', remainingCookies.unexpectedCookies);
          // Log for debugging but don't fail - this helps identify issues
        }
        
        console.log('📅 DAILY CLEAR COMPLETE: Game state cleared successfully. Streak data preserved:', remainingCookies.streakCookies);
      } else {
        console.log('New day detected but in practice mode - preserving game state');
      }
    }

    return { isNewDay, effectiveDate };
  } catch (error) {
    console.error('Error in handleNewDay:', error);
    throw error;
  }
};

// Validate that only streak cookies remain after clearing
const validateStreakOnlyRemaining = (mode: 'daily' | 'practice') => {
  const prefix = mode === 'daily' ? 'babblegram_daily_' : 'babblegram_practice_';
  const streakKeys = ['streak', 'max_streak', 'last_win_date', 'previous_streak'];
  const expectedStreakCookies = streakKeys.map(key => `${prefix}${key}`);
  
  // Get all cookies for this mode
  const allCookies = document.cookie.split(';')
    .map(cookie => cookie.trim().split('=')[0])
    .filter(name => name.startsWith(prefix));
  
  const streakCookies = allCookies.filter(name => 
    expectedStreakCookies.some(expected => name === expected)
  );
  
  const unexpectedCookies = allCookies.filter(name => 
    !expectedStreakCookies.some(expected => name === expected)
  );
  
  return {
    streakCookies,
    unexpectedCookies,
    totalCookies: allCookies.length
  };
};

// Validate that new day clearing was successful (call when no guesses made on new day)
export const validateNewDayClearing = (currentMode: 'daily' | 'practice' = 'daily'): boolean => {
  if (currentMode !== 'daily') {
    console.log('📋 CLEARING VALIDATION: Skipping - not in daily mode');
    return true;
  }

  console.group('📋 NEW DAY CLEARING VALIDATION');
  
  // Check if no game state cookies exist for daily mode
  const gameStateCookies = [
    'guess_history', 'level_attempts', 'keyboard_status', 'previous_word',
    'current_word', 'current_level', 'game_status', 'game_completion_state'
  ];
  
  const dailyPrefix = 'babblegram_daily_';
  const foundGameState = gameStateCookies.filter(key => {
    const cookieName = `${dailyPrefix}${key}`;
    const exists = document.cookie.includes(cookieName);
    if (exists) {
      console.warn(`⚠️ Found unexpected game state cookie: ${cookieName}`);
    }
    return exists;
  });
  
  // Check what cookies do exist
  const validation = validateStreakOnlyRemaining('daily');
  
  const clearingSuccessful = foundGameState.length === 0;
  
  console.log('📊 CLEARING VALIDATION RESULTS:', {
    clearingSuccessful,
    unexpectedGameStateCookies: foundGameState,
    expectedStreakCookies: validation.streakCookies,
    unexpectedOtherCookies: validation.unexpectedCookies,
    totalDailyCookies: validation.totalCookies
  });
  
  if (clearingSuccessful) {
    console.log('✅ CLEARING SUCCESSFUL: No game state cookies found - midnight clearing worked correctly');
  } else {
    console.warn('❌ CLEARING FAILED: Found game state cookies that should have been cleared at midnight');
  }
  
  console.groupEnd();
  
  return clearingSuccessful;
};

// Re-run daily clearing if validation failed
export const rerunDailyClearing = async (): Promise<boolean> => {
  console.group('🔄 RE-RUNNING DAILY CLEARING');
  console.log('🧹 Attempting to clear daily game state after validation failure...');
  
  try {
    // Clear daily mode cookies specifically
    const dailyCookies = ['guess_history', 'level_attempts', 'game_status', 'current_word', 
                         'current_level', 'keyboard_status', 'previous_word', 'game_completion_state'];
    
    dailyCookies.forEach(key => {
      modeAwareStorage.removeCookieForMode(key, 'daily');
      console.log(`🗑️ Cleared: ${key}`);
    });
    
    // Clear game state through the persistence layer
    await clearGameState();
    console.log('✅ Game state cleared successfully');
    
    // Set persistence date for today
    const today = formatDate(getEffectiveDate());
    modeAwareStorage.setCookie('game_persistence_date', today);
    console.log('📅 Persistence date set:', today);
    
    // Validate the clearing worked
    const validation = validateNewDayClearing('daily');
    
    if (validation) {
      console.log('✅ RE-CLEARING SUCCESSFUL: Daily state properly cleared');
    } else {
      console.error('❌ RE-CLEARING FAILED: Some cookies still remain');
    }
    
    console.groupEnd();
    return validation;
    
  } catch (error) {
    console.error('❌ Error during daily clearing re-run:', error);
    console.groupEnd();
    return false;
  }
};

// Get the correct day of year using pure calendar math (immune to DST)
export const getDayOfYear = (date: Date): number => {
  const normalizedDate = normalizeDate(date);
  const year = normalizedDate.getFullYear();
  const month = normalizedDate.getMonth(); // 0-indexed
  const day = normalizedDate.getDate();
  
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  let dayOfYear = day;
  for (let i = 0; i < month; i++) {
    dayOfYear += daysInMonth[i];
  }
  
  return dayOfYear;
};

// Set up background countdown to next day - Uses TimeoutManager for safety
if (typeof window !== 'undefined') {
  function setupNextDayCountdown() {
    // Clear any existing midnight countdown to prevent duplicates
    timeoutManager.clearTimeout('midnightReset');
    
    // Use regular date to avoid circular reference
    const now = new Date(); 
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();
    
    console.log('Setting up next day countdown:', {
      now: now.toISOString(),
      tomorrow: tomorrow.toISOString(),
      timeUntilMidnight,
      timestamp: new Date().toISOString()
    });

    // Use the timeout manager to prevent race conditions
    timeoutManager.setTimeout('midnightReset', () => {
      console.log('🌙 MIDNIGHT REACHED - Starting daily state clear process');
      
      // Clear any other active timeouts before proceeding
      timeoutManager.clearAllTimeouts();
      
      // Set midnight transition flag to prevent completion screen persistence
      localStorage.setItem('babblegram_midnight_transition', 'true');
      
      // CRITICAL FIX: Always clear DAILY mode state at midnight regardless of current UI mode
      // This prevents users who switch between modes from having stale daily state
      const currentUIMode = localStorage.getItem('babblegram_game_mode') || 'daily';
      console.log('🌙 MIDNIGHT CLEAR: Clearing daily mode state regardless of UI mode', {
        currentUIMode,
        action: 'clearing_daily_state',
        timestamp: new Date().toISOString()
      });
      
      // Force clear daily mode cookies specifically
      const dailyCookies = ['guess_history', 'level_attempts', 'game_status', 'current_word', 
                           'current_level', 'keyboard_status', 'previous_word', 'game_completion_state'];
      
      dailyCookies.forEach(key => {
        modeAwareStorage.removeCookieForMode(key, 'daily');
      });
      
      // Set new persistence date for daily mode after clearing
      const todayFormatted = formatDate(new Date());
      modeAwareStorage.setCookieForMode('game_persistence_date', todayFormatted, 'daily');
      
      console.log('🌙 MIDNIGHT CLEAR: Daily mode cookies cleared and persistence date set', { 
        newDate: todayFormatted 
      });
      window.location.reload();
    }, timeUntilMidnight);
  }

  // Start the countdown when the page loads
  setupNextDayCountdown();
}

// Export function to wait for daily reset barrier
export const waitForDailyResetBarrier = async (): Promise<void> => {
  if (dailyResetBarrier) {
    console.log('[waitForDailyResetBarrier] Waiting for daily reset to complete...');
    await dailyResetBarrier;
    console.log('[waitForDailyResetBarrier] Daily reset completed');
  }
};

// Debug function to simulate midnight for testing
export function simulateMidnight() {
  if (typeof window === 'undefined') return;
  
  console.log('🔥 DEBUG - Simulating midnight transition in 5 seconds');
  
  // Clear existing countdown
  timeoutManager.clearTimeout('midnightReset');
  
  // First advance the debug date to the next day
  const currentDebugDate = getDebugDate();
  
  if (currentDebugDate) {
    const nextDay = new Date(currentDebugDate);
    nextDay.setDate(nextDay.getDate() + 1);
    
    console.log('🔥 DEBUG - Advancing debug date for midnight simulation:', {
      from: currentDebugDate.toISOString(),
      to: nextDay.toISOString()
    });
    
    setDebugDate(nextDay);
  }
  
  // Set midnight transition flag
  localStorage.setItem('babblegram_midnight_transition', 'true');
  
  // Trigger the midnight logic in 5 seconds
  timeoutManager.setTimeout('midnightReset', async () => {
    console.group('🔥 SIMULATED MIDNIGHT - Cookie Clearing Process');
    console.log('🔥 DEBUG - Simulated midnight reached, processing state changes');
    
    // Clear any other active timeouts before proceeding
    timeoutManager.clearAllTimeouts();
    
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    console.log('📊 Current UI mode:', currentMode);
    
    try {
      // ALWAYS clear DAILY namespace (aligned with auto-midnight behavior)
      console.group('🧹 Clearing DAILY game state');
      console.log('🗑️ Clearing DAILY namespace cookies (regardless of current UI mode)...');
      
      const { modeAwareStorage } = await import('@/lib/managers/ModeAwareStorageManager');
      modeAwareStorage.clearModeStateForMode('daily');
      
      console.log('✅ DAILY namespace cleared successfully');
      console.groupEnd();
      
      // Update persistence date
      console.group('📅 Updating persistence date');
      const effectiveDate = formatDate(getEffectiveDate());
      console.log('🗓️ Setting persistence date:', effectiveDate);
      
      modeAwareStorage.setPersistenceDate(effectiveDate);
      console.log('✅ Persistence date updated');
      console.groupEnd();
      
      // Validate remaining cookies
      console.group('🔍 Cookie validation');
      console.log('📋 Checking remaining cookies after clear...');
      
      const allCookies = document.cookie.split(';').map(c => c.trim()).filter(c => c);
      const babblegram_cookies = allCookies.filter(c => c.startsWith('babblegram_'));
      
      console.log('🍪 Total cookies remaining:', allCookies.length);
      console.log('🎯 Babblegram cookies remaining:', babblegram_cookies.length);
      console.log('📝 Babblegram cookies:', babblegram_cookies.map(c => c.split('=')[0]).join(', '));
      console.groupEnd();
      
    } catch (error) {
      console.error('❌ Error during simulated midnight processing:', error);
    }
    
    console.log('🔄 Preparing page reload in 2 seconds to flush logs...');
    console.groupEnd();
    
    // Add delay before reload to ensure logs are flushed
    timeoutManager.setTimeout('simulatedMidnightReload', () => {
      console.log('🔄 Reloading page for simulated midnight with debug mode');
      window.location.href = `${window.location.pathname}?debug=date`;
    }, 2000); // 2 seconds delay to ensure logs are visible
  }, 5000); // 5 seconds
}
